import { FontWeights, Icon, IconButton, Stack, Text } from '@fluentui/react';
import React, { useContext, useState } from 'react';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../../Common/api/ABHub/ABHubGlobalConfig';
import { CloudEnvironment } from '../../../../Common/models/CloudEnvironment';
import { dateLocaleStringWithTimeZone } from '../../../../Common/util/DateUtils';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';

/**
 * The CloudVault artifact metadata SRX retrieves from the CloudVault REST API in public
 * and writes onto the build manifest.
 */
export interface CloudVaultArtifact {
    artifactId?: string;
    vaultName?: string;
    dateCreated?: string;
    metadata?: { [key: string]: string };
}

/**
 * Parses the decoded CloudVault artifact metadata off a build metadata value.
 * Returns undefined when the value isn't the CloudVault json, so callers can fall back
 * to rendering it as a plain metadata row.
 */
export const parseCloudVaultArtifact = (
    value: string
): CloudVaultArtifact | undefined => {
    const trimmed = value?.trim();

    if (!trimmed || !trimmed.startsWith('{')) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(trimmed);

        if (typeof parsed !== 'object' || parsed === null) {
            return undefined;
        }

        // Unrecognised json falls back to a plain metadata row instead of an empty card.
        const isCloudVaultShape =
            'artifactId' in parsed ||
            'vaultName' in parsed ||
            'dateCreated' in parsed ||
            'metadata' in parsed;

        return isCloudVaultShape ? (parsed as CloudVaultArtifact) : undefined;
    } catch {
        return undefined;
    }
};

export interface ICloudVaultCardProps {
    artifact: CloudVaultArtifact;
    /** The single environment this stage targets. A stage only ever has one. */
    environment?: CloudEnvironment;
    /** Artifact id from the sovereign vault. Only ever populated in region. */
    uploadedArtifactId?: string;
    /** When the sovereign upload completed. Absent until the upload succeeds. */
    uploadedOn?: string | Date;
}

export const CloudVaultCard: React.FC<ICloudVaultCardProps> = (
    props: ICloudVaultCardProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [isExpanded, setIsExpanded] = useState(true);

    const { artifact, environment, uploadedArtifactId, uploadedOn } = props;
    const shared = artifact.metadata ?? {};

    // The upload timestamp is what tells us the upload finished - the id is redacted in public.
    const uploaded = !!uploadedOn;

    // Pointing the customer at the in-cloud portal only helps from outside that cloud. This same
    // card renders in the sovereign portal, where the id is simply absent on older schemas.
    const portalCloud = getConfig(GlobalConfigProperties.Cloud);
    const inRegion =
        !!environment &&
        !!portalCloud &&
        portalCloud.toLowerCase() === environment.toLowerCase();

    const cardStyles: React.CSSProperties = {
        padding: '16px',
        backgroundColor: theme.palette.white,
        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
        borderRadius: '4px',
    };

    const labelStyles = {
        root: {
            fontWeight: FontWeights.bold,
            width: 140,
            flexShrink: 0,
            color: theme.palette.neutralSecondary,
        },
    };

    const cloudWidth = 90;
    const artifactIdWidth = 340;
    const createdWidth = 190;
    const statusWidth = 130;

    const fieldRow = (label: string, value: string | undefined) => (
        <Stack
            horizontal
            tokens={{ childrenGap: 8 }}
            verticalAlign="center"
            styles={{ root: { minHeight: 28 } }}
        >
            <Text variant="small" styles={labelStyles}>
                {label}
            </Text>
            <Text variant="small" styles={{ root: { wordBreak: 'break-all' } }}>
                {value || '—'}
            </Text>
        </Stack>
    );

    // Header and data rows share this so the columns line up.
    const tableRow = (
        key: string,
        cloud: React.ReactNode,
        artifactId: React.ReactNode,
        created: React.ReactNode,
        status: React.ReactNode
    ) => (
        <Stack
            key={key}
            horizontal
            tokens={{ childrenGap: 8 }}
            verticalAlign="center"
            styles={{ root: { minHeight: 28 } }}
        >
            <div style={{ width: cloudWidth, flexShrink: 0 }}>{cloud}</div>
            <div style={{ width: artifactIdWidth, flexShrink: 0 }}>
                {artifactId}
            </div>
            <div style={{ width: createdWidth, flexShrink: 0 }}>{created}</div>
            <div style={{ width: statusWidth, flexShrink: 0 }}>{status}</div>
        </Stack>
    );

    const headerCell = (text: string) => (
        <Text
            variant="small"
            styles={{
                root: {
                    fontWeight: FontWeights.bold,
                    color: theme.palette.neutralSecondary,
                },
            }}
        >
            {text}
        </Text>
    );

    const cell = (text: string) => <Text variant="small">{text}</Text>;

    const artifactIdCell = (cloud: string, artifactId?: string) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Text variant="small" styles={{ root: { wordBreak: 'break-all' } }}>
                {artifactId || '—'}
            </Text>
            {artifactId && (
                <IconButton
                    iconProps={{ iconName: 'Copy' }}
                    title={`Copy ${cloud} CloudVault artifact id to clipboard`}
                    ariaLabel={`Copy ${cloud} CloudVault artifact id`}
                    styles={{
                        root: { width: 24, height: 24 },
                        icon: { fontSize: 12 },
                    }}
                    onClick={() => navigator.clipboard.writeText(artifactId)}
                />
            )}
        </Stack>
    );

    return (
        <div style={cardStyles}>
            <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
                styles={{ root: { marginBottom: isExpanded ? 12 : 0 } }}
            >
                <IconButton
                    iconProps={{
                        iconName: isExpanded
                            ? 'ChevronDownMed'
                            : 'ChevronRightMed',
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                    title={
                        isExpanded
                            ? 'Hide CloudVault details'
                            : 'Show CloudVault details'
                    }
                    ariaLabel={
                        isExpanded
                            ? 'Hide CloudVault details'
                            : 'Show CloudVault details'
                    }
                    aria-expanded={isExpanded}
                    styles={{
                        root: { width: 24, height: 24 },
                        icon: { fontSize: 12 },
                    }}
                />
                <Icon
                    iconName="Cloud"
                    styles={{
                        root: {
                            fontSize: 16,
                            color: theme.palette.themePrimary,
                        },
                    }}
                />
                <Text
                    variant="mediumPlus"
                    styles={{ root: { fontWeight: 600 } }}
                >
                    CloudVault
                </Text>
            </Stack>

            {isExpanded && (
                <>
                    {/* Identity of the artifact - identical in every cloud, so shown once. */}
                    <Stack tokens={{ childrenGap: 4 }}>
                        {fieldRow('Vault', artifact.vaultName)}
                        {fieldRow('Product', shared.product)}
                        {fieldRow('Branch', shared.branch)}
                        {fieldRow('Build Number', shared.buildnumber)}
                        {fieldRow('Service Tree', shared.servicetreeid)}
                    </Stack>

                    <div
                        style={{
                            borderTop: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
                            margin: '12px 0',
                        }}
                    />

                    {/* The id and creation date differ per cloud - the upload creates a new artifact. */}
                    <Stack tokens={{ childrenGap: 4 }}>
                        {tableRow(
                            'header',
                            headerCell('Cloud'),
                            headerCell('Artifact Id'),
                            headerCell('Created'),
                            headerCell('Status')
                        )}
                        {tableRow(
                            'public',
                            cell(CloudEnvironment.Public),
                            artifactIdCell(
                                CloudEnvironment.Public,
                                artifact.artifactId
                            ),
                            cell(
                                artifact.dateCreated
                                    ? dateLocaleStringWithTimeZone(
                                          artifact.dateCreated
                                      )
                                    : '—'
                            ),
                            cell('Source')
                        )}
                        {environment &&
                            tableRow(
                                environment,
                                cell(environment),
                                uploaded && !uploadedArtifactId && !inRegion ? (
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                                fontStyle: 'italic',
                                            },
                                        }}
                                    >
                                        {`See SRM Portal in ${environment}`}
                                    </Text>
                                ) : (
                                    artifactIdCell(
                                        environment,
                                        uploadedArtifactId
                                    )
                                ),
                                cell(
                                    uploadedOn
                                        ? dateLocaleStringWithTimeZone(
                                              uploadedOn
                                          )
                                        : '—'
                                ),
                                cell(uploaded ? 'Upload Complete' : 'Pending')
                            )}
                    </Stack>
                </>
            )}
        </div>
    );
};
